/**
 * ShareReferralLink Component
 * 
 * A component for sharing referral links with various social media platforms and QR code generation.
 * 
 * Usage:
 * ```tsx
 * import { ShareReferralLink } from './Affliate';
 * 
 * const MyComponent = () => {
 *   const referralLink = "https://invite.airdna.co/ARNAV-BIS...";
 *   
 *   return <ShareReferralLink referralLink={referralLink} />;
 * };
 * ```
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Alert,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import CloseIcon from '@mui/icons-material/Close';

interface ShareReferralLinkProps {
  referralLink: string;
}

const ShareReferralLink: React.FC<ShareReferralLinkProps> = ({ referralLink }) => {
  const [copySuccess, setCopySuccess] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess('Link copied!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handlePrepareEmail = () => {
    setSubject('Discover Airbtics - Your Key to Short-Term Rental Success');
    setMessage(`Hi there!\n\nI wanted to share something that's been a game-changer for my short-term rental investments - Airbtics!\n\nAirbtics provides powerful data and insights that help you make smarter decisions whether you're buying your first rental property or optimizing your existing portfolio. The platform has helped me identify profitable markets, set competitive pricing, and maximize my returns.\n\nI think you'd find it incredibly valuable for your investment journey. You can check it out using my referral link below:\n\n${referralLink}\n\nBest regards!`);
  };

  const handleFacebookShare = () => {
    setSubject('Transform Your Short-Term Rental Business');
    setMessage(`🏠 Ready to take your short-term rental game to the next level?\n\nI've been using Airbtics to make data-driven decisions for my rental properties, and the results have been amazing! From market analysis to pricing optimization, this platform has everything you need to succeed.\n\n✨ Join me on Airbtics: ${referralLink}\n\n#Airbtics #ShortTermRentals #RealEstateInvesting #PassiveIncome`);
  };

  const handleTwitterShare = () => {
    setSubject('Airbtics Recommendation');
    setMessage(`🚀 Level up your short-term rental business with Airbtics! \n\nData-driven insights for smarter property investments and pricing strategies. Join me: ${referralLink}\n\n#Airbtics #STR #RealEstate #PropertyInvesting`);
  };

  const handleLinkedInShare = () => {
    setSubject('Maximizing Short-Term Rental ROI with Data-Driven Insights');
    setMessage(`As a real estate investor focused on short-term rentals, I'm always looking for tools that give me a competitive edge. That's why I've been using Airbtics.\n\nAirbtics provides comprehensive market data, competitive analysis, and pricing insights that have significantly improved my investment decisions. Whether you're evaluating new markets, optimizing your current properties, or planning your next acquisition, this platform delivers the data you need to succeed.\n\nThe insights I've gained have directly contributed to better occupancy rates and higher revenues across my portfolio.\n\nIf you're serious about short-term rental investing, I highly recommend checking it out: ${referralLink}\n\n#RealEstateInvesting #ShortTermRentals #PropertyManagement #DataDriven #Airbtics`);
  };

  const handleCloseEditor = () => {
    setSubject('');
    setMessage('');
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 4, 
        border: '1px solid #e0e0e0', 
        borderRadius: 2,
        maxWidth: 600,
        mx: 'auto'
      }}
    >
      {/* Title */}
      <Typography 
        variant="h6" 
        sx={{ 
          textAlign: 'center', 
          mb: 3, 
          fontWeight: 600,
          color: '#333'
        }}
      >
        Your Referral Link:
      </Typography>

      {/* Referral Link Input */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          value={referralLink}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  onClick={handleCopy}
                  sx={{ 
                    bgcolor: '#000', 
                    color: 'white',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: '#333',
                    }
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
              },
            }
          }}
        />
        
        {copySuccess && (
          <Alert 
            severity="success" 
            sx={{ mt: 1, borderRadius: 1 }}
          >
            {copySuccess}
          </Alert>
        )}
      </Box>

      {/* Subject and Message Inputs - Only show when content exists */}
      {(subject || message) && (
        <Box sx={{ mb: 3, position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              Edit Your Message
            </Typography>
            <IconButton 
              onClick={handleCloseEditor}
              sx={{ 
                bgcolor: '#f5f5f5',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Box>
      )}

      {/* QR Code */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          component="img"
          src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(referralLink)}`}
          alt="QR Code for referral link"
          sx={{
            width: 150,
            height: 150,
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '8px',
            bgcolor: 'white'
          }}
        />
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1, 
            color: '#666',
            fontWeight: 500
          }}
        >
          Personal Share Code
        </Typography>
      </Box>

      {/* Social Share Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<EmailIcon />}
          onClick={() => {
            if (subject && message) {
              const encodedSubject = encodeURIComponent(subject);
              const encodedBody = encodeURIComponent(message);
              window.open(`mailto:?subject=${encodedSubject}&body=${encodedBody}`, '_blank');
            } else {
              handlePrepareEmail();
            }
          }}
          sx={{
            bgcolor: '#4A5568',
            color: 'white',
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': {
              bgcolor: '#2D3748',
            },
          }}
        >
          {subject && message ? 'Send Email' : 'Prepare Email'}
        </Button>

        <Button
          fullWidth
          variant="contained"
          startIcon={<FacebookIcon />}
          onClick={() => {
            if (subject && message) {
              const shareText = encodeURIComponent(message);
              window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${shareText}`, '_blank', 'width=600,height=400');
            } else {
              handleFacebookShare();
            }
          }}
          sx={{
            bgcolor: '#4267B2',
            color: 'white',
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': {
              bgcolor: '#365899',
            },
          }}
        >
          {subject && message ? 'Post to Facebook' : 'Prepare Facebook Post'}
        </Button>

        <Button
          fullWidth
          variant="contained"
          startIcon={<TwitterIcon />}
          onClick={() => {
            if (subject && message) {
              const shareText = encodeURIComponent(message);
              window.open(`https://twitter.com/intent/tweet?text=${shareText}`, '_blank', 'width=600,height=400');
            } else {
              handleTwitterShare();
            }
          }}
          sx={{
            bgcolor: '#000000',
            color: 'white',
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': {
              bgcolor: '#333333',
            },
          }}
        >
          {subject && message ? 'Share on X' : 'Prepare X Post'}
        </Button>

        <Button
          fullWidth
          variant="contained"
          startIcon={<LinkedInIcon />}
          onClick={() => {
            if (subject && message) {
              // LinkedIn sharing is limited, so we'll use the basic share with URL
              window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, '_blank', 'width=600,height=400');
            } else {
              handleLinkedInShare();
            }
          }}
          sx={{
            bgcolor: '#0077B5',
            color: 'white',
            py: 1.5,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': {
              bgcolor: '#005885',
            },
          }}
        >
          {subject && message ? 'Share on LinkedIn' : 'Prepare LinkedIn Post'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ShareReferralLink;
